import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../../Common/Fader/FormFader";
import Grid from "@mui/material/Grid";
import { minNumber, requiredValidator } from "../../../../services/validationRules";
import FormSubmit from "../../Common/FormSubmit/FormSubmit";
import DialogModal from "../../../DialogModal/DialogModal";
import { useForm } from "../../../../provider/Form/FormProvider";
import Person from "../../Common/Person/Person";
import { useLocation, useNavigate } from "react-router";
import Typography from "@mui/material/Typography";
import FormLegend from "../../Common/FormLegend/FormLegend";
import NumberFieldConfigured from "../../ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "../../../../types/FormSubmitProps";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import { Box, Button } from "@mui/material";
import wizardStyle from "../../wizardStyle";
import InfoIconButton from "../../../Button/InfoIconButton";
import Group from "../../../Wizard/Group";
import CheckIcon from "../../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../../assets/icons/common/toggleable/IconClose";
import IconCompany from "../../../../assets/icons/contract/toggleable/IconCompany";
import IconClose from "../../../../assets/icons/common/toggleable/IconClose";
import IconDocumentHouseSignDetailed from "../../../../assets/icons/provision/toggleable/IconDocumentHouseSignDetailed";
import IconInheritance from "../../../../assets/icons/familyLaw/toggleable/IconInheritance";
import IconHouse from "../../../../assets/icons/provision/toggleable/IconHouse";
import AlimonyExpenses from "./AlimonyExpenses/AlimonyExpenses";
import IconMoneyAndWork from "../../../../assets/icons/employment/toggleable/IconMoneyAndWork";
import MoreInfoIcon from "../../../SvgIcon/MoreInfoIcon";
import groupStyle from "../../../Wizard/groupStyle";

type AlimonyPersonProps = { personType: string; submitProps: FormSubmitProps; product: AbstractCase };

export default function AlimonyPerson({ personType, submitProps, product }: AlimonyPersonProps) {
  const { values, handleSubmit, handleChange } = useForm();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (values[personType].hasOtherIncome === "no") {
      handleChange({
        target: {
          name: `${personType}.otherIncome`,
          value: "0",
          type: "text",
        },
      });
    }
  }, [values[personType].hasOtherIncome]);

  useEffect(() => {
    if (values[personType].otherIncome !== null) {
      handleChange({
        target: {
          name: `${personType}.hasOtherIncome`,
          value: values[personType].otherIncome > 0 ? "yes" : "no",
          type: "text",
        },
      });
    }
  }, []);

  const modalOpen = (modalType: string) => {
    navigate(location.pathname, { state: { rentModalOpen: modalType } });
  };
  const modalClose = () => {
    navigate(-1);
  };

  const getNetIncomeHelperText = () => {
    if (values.alimonyPetitioner?.employmentStatus === "selfEmployed") {
      return (
        <>
          Ermitteln Sie bitte Ihr durchschnittliches monatliches Nettoeinkommen der letzten 36 Monate. Beachten Sie hierzu auch die weiteren Informationen nach
          Klick auf den Info-Button rechts.
          <br />
          <strong>Hinweis:</strong> Sollte das Einkommen unbekannt sein, ist zur Gewährleistung der weiteren Berechnung eine{" "}
          <strong>plausible Schätzung</strong> erforderlich. <strong>Die Eingabe von 0€ ist nicht möglich.</strong>
        </>
      );
    } else {
      return (
        <>
          Geben Sie hier bitte Ihr durchschnittliches monatliches Gehalt der letzten 12 Monate an (Boni, Zulagen, Weihnachtsgeld usw. einberechnet).
          <br />
          <strong>Hinweis:</strong> Sollte das Einkommen unbekannt sein, ist zur Gewährleistung der weiteren Berechnung eine{" "}
          <strong>plausible Schätzung</strong> erforderlich. <strong>Die Eingabe von 0€ ist nicht möglich.</strong>
        </>
      );
    }
  };
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h5"} sx={wizardStyle.headline}>
            {personType === "alimonyPetitioner" ? "Ihre finanzielle Situation" : "Finanzielle Situation des anderen Elternteils"}
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
              mb: 5
            }}>
            {personType === "alimonyPartner" && <Person path="alimonyPartner.person" headline="Wie heißt der andere Elternteil?" />}

            {personType === "alimonyPartner" && (
              <Grid size={12}>
                <Group
                  type="radio"
                  name={"alimonyPartner.employmentStatus"}
                  question={"Ist der andere Elternteil erwerbstätig?"}
                  options={[
                    {
                      labelText: "Ja",
                      labelIcon: <CheckIcon />,
                      labelIconActive: <CheckIcon active />,
                      value: "employed",
                    },
                    {
                      labelText: "Nein",
                      labelIcon: <IconClose />,
                      labelIconActive: <IconClose active />,
                      value: "unemployed",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}

            {personType === "alimonyPetitioner" && (
              <Grid size={12}>
                <Group
                  type="radio"
                  name={"alimonyPetitioner.employmentStatus"}
                  question={"Wovon beziehen Sie Ihren Lebensunterhalt?"}
                  options={[
                    {
                      labelText: "Ich bin erwerbstätig (angestellt)",
                      labelIcon: <IconMoneyAndWork />,
                      labelIconActive: <IconMoneyAndWork active />,
                      value: "employed",
                    },
                    {
                      labelText: "Ich bin selbstständig",
                      labelIcon: <IconCompany />,
                      labelIconActive: <IconCompany active />,
                      value: "selfEmployed",
                    },
                    {
                      labelText: "Ich bin (zurzeit) weder erwerbstätig noch selbstständig",
                      labelIcon: <IconClose />,
                      labelIconActive: <IconClose active />,
                      value: "unemployed",
                      optionHintText: "z.B. Student/in, Rentner/in, arbeitssuchend oder berufsunfähig",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}

            {values.alimonyParter?.employmentStatus !== null && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>
                    {personType === "alimonyPetitioner"
                      ? "Wie hoch ist Ihr monatliches Nettoeinkommen?"
                      : "Wie hoch ist das monatliche Nettoeinkommen des anderen Elternteils?"}

                    <InfoIconButton
                      onClick={() =>
                        modalOpen(values.alimonyPetitioner?.employmentStatus === "selfEmployed" ? "netIncome-selfEmployed" : `netIncome-${personType}`)
                      }
                    />
                  </FormLegend>
                </Grid>

                <Grid container size={12}>
                  <NumberFieldConfigured
                    caseData={product}
                    fieldPath={`${personType}.netIncome`}
                    fieldType={"money"}
                    numberType={"float"}
                    gridItemXs={12}
                    customHelperText={getNetIncomeHelperText()}
                    validators={[{ validator: minNumber, params: [1], message: "Das Einkommen darf nicht “0” sein." }]}
                  />
                </Grid>

                <Grid size={12}>
                  <Group
                    type="radio"
                    name={`${personType}.hasOtherIncome`}
                    question={
                      personType === "alimonyPetitioner"
                        ? "Haben Sie sonstiges einkommenssteuerpflichtiges Einkommen, z.B. aus Vermietung oder Verpachtung?"
                        : "Hat der andere Elternteil sonstiges einkommenssteuerpflichtiges Einkommen, z.B. aus Vermietung oder Verpachtung?"
                    }
                    options={[
                      {
                        labelText: "Ja",
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                      {
                        labelText: "Nein",
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>

                {values[personType].hasOtherIncome === "yes" && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>
                        {personType === "alimonyPetitioner"
                          ? "Wie hoch ist Ihr weiteres Einkommen?"
                          : "Wie hoch ist das weitere Einkommen des anderen Elternteils?"}

                        <InfoIconButton onClick={() => modalOpen("otherIncome")} />
                      </FormLegend>
                    </Grid>

                    <Grid container size={12}>
                      <NumberFieldConfigured
                        caseData={product}
                        fieldPath={`${personType}.otherIncome`}
                        fieldType={"money"}
                        numberType={"float"}
                        gridItemXs={12}
                      />
                    </Grid>
                  </>
                )}

                {values.basicChoice === "complete" && (
                  <Grid size={12}>
                    <Group
                      type="radio"
                      name={`${personType}.hasMarriageRelatedDisadvantages`}
                      question={"Liegen ehebedingte Nachteile vor?"}
                      modalHeaderText={"Was sind ehebedingte Nachteile?"}
                      moreInfoText={"Was sind ehebedingte Nachteile?"}
                      modalContent={
                        "Ein ehebedingter Nachteil entsteht meistens, wenn ein Ehepartner, der vor der Ehe berufstätig war, während der Ehe die Haushaltsführung und die\n" +
                        "          Kinderbetreuung übernimmt und dadurch nicht mehr arbeiten geht. Er gibt damit seine berufliche Karriere und die damit verbundenen\n" +
                        "          Einkommenssteigerungen auf. Wenn er nach der Ehe wieder berufstätig wird, fängt er sozusagen von vorne an. Er verdient jedenfalls deutlich weniger,\n" +
                        "          als wenn er während der Ehe die gesamte Zeit durchgearbeitet hätte. In einem solchen Fall kann ein ehebedingter Nachteil vorliegen."
                      }
                      options={[
                        {
                          labelText: "Ja",
                          labelIcon: <CheckIcon />,
                          labelIconActive: <CheckIcon active />,
                          value: "yes",
                        },
                        {
                          labelText: "Nein",
                          labelIcon: <CrossIcon />,
                          labelIconActive: <CrossIcon active />,
                          value: "no",
                        },
                      ]}
                      validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                    />
                  </Grid>
                )}

                <Grid size={12}>
                  <Group
                    type="radio"
                    name={`${personType}.livingSituation`}
                    question={personType === "alimonyPetitioner" ? "Wie ist Ihre Wohnsituation?" : "Wie ist die Wohnsituation des anderen Elternteils?"}
                    options={[
                      {
                        labelText: personType === "alimonyPetitioner" ? "Ich wohne zur Miete" : "Der andere Elternteil wohnt zur Miete",
                        labelIcon: <IconDocumentHouseSignDetailed />,
                        labelIconActive: <IconDocumentHouseSignDetailed active />,
                        value: "tenant",
                      },
                      {
                        labelText:
                          personType === "alimonyPetitioner"
                            ? "Ich wohne in einer Eigentumsimmobilie, welche ich derzeit abbezahle"
                            : "Der andere Elternteil wohnt in einer Eigentumsimmobilie, welche er derzeit abbezahlt",
                        labelIcon: <IconInheritance />,
                        labelIconActive: <IconInheritance active />,
                        value: "ownerWithMortgage",
                      },
                      {
                        labelText:
                          personType === "alimonyPetitioner"
                            ? "Ich wohne in einer vollständig abbezahlten Eigentumsimmobilie"
                            : "Der andere Elternteil wohnt in einer vollständig abbezahlten Eigentumsimmobilie",
                        labelIcon: <IconHouse />,
                        labelIconActive: <IconHouse active />,
                        value: "ownerOutright",
                      },
                      {
                        labelText:
                          personType === "alimonyPetitioner"
                            ? "Ich wohne weder zur Miete noch in einer Eigentumsimmobilie"
                            : "Der andere Elternteil wohnt weder zur Miete noch in einer Eigentumsimmobilie",
                        labelIcon: <IconClose />,
                        labelIconActive: <IconClose active />,
                        value: "other",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>

                {values[personType].livingSituation === "tenant" && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>
                        {personType === "alimonyPetitioner"
                          ? "Wie hoch ist Ihre monatliche Warmmiete?"
                          : "Wie hoch ist die monatliche Warmmiete des anderen Elternteils?"}

                        <InfoIconButton onClick={() => modalOpen("livingExpenses")} />
                      </FormLegend>
                    </Grid>

                    <Grid container size={12}>
                      <NumberFieldConfigured
                        caseData={product}
                        fieldPath={`${personType}.livingExpenses`}
                        fieldType={"money"}
                        numberType={"float"}
                        gridItemXs={12}
                        required
                      />
                    </Grid>
                  </>
                )}
                {values[personType].livingSituation === "ownerWithMortgage" && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>Wie hoch ist die monatliche Kreditrate (Tilgung und Zins)?</FormLegend>
                    </Grid>

                    <Grid container size={12}>
                      <NumberFieldConfigured
                        caseData={product}
                        fieldPath={`${personType}.monthlyMortgagePayment`}
                        fieldType={"money"}
                        numberType={"float"}
                        gridItemXs={12}
                        required
                      />
                    </Grid>
                  </>
                )}
                {["ownerWithMortgage", "ownerOutright", "other"].includes(values[personType].livingSituation) && (
                  <>
                    <FormLegend>
                      {"Wie hoch ist der Wohnwert?"}

                      <InfoIconButton onClick={() => modalOpen("monthlyImputedRent-" + values[personType].livingSituation)} />
                    </FormLegend>

                    <Grid container size={12}>
                      <NumberFieldConfigured
                        caseData={product}
                        fieldPath={`${personType}.monthlyImputedRent`}
                        fieldType={"money"}
                        numberType={"float"}
                        gridItemXs={12}
                        required
                      />
                    </Grid>
                  </>
                )}

                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{"Weitere relevante Ausgaben"}</FormLegend>
                  <Button fullWidth={true} variant={"text"} color={"primary"} onClick={() => modalOpen("monthlyExpenses")}>
                    <MoreInfoIcon sx={groupStyle.leftIcon} /> Weitere Informationen
                  </Button>
                </Grid>

                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <Typography variant={"body1"} sx={{
                    textAlign: "center"
                  }}>
                    <strong>
                      Bestimmte Ausgaben, wie z.B. notwendige Versicherungen und die Tilgung von Schulden, können das unterhaltsrelevante Einkommen mindern,
                      sofern sie einen konkreten Bezug zur Ehe und/oder den Kindern haben.
                      <br />
                      <br />
                      Bitte geben Sie hier, sofern vorhanden, Ihre relevanten monatlichen Ausgaben an
                    </strong>
                  </Typography>
                </Grid>

                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <AlimonyExpenses product={product} personType={personType} />
                </Grid>
              </>
            )}
          </Grid>

          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "netIncome-selfEmployed"}
        onClose={modalClose}
        title={"Nettoeinkommen von Selbstständigen"}
      >
        <p>
          Die Berechnung des relevanten Einkommens bei Selbstständigen ist komplex und es müssen mehrere Unterlagen einbezogen werden, z.B. die Bilanz, Gewinn-
          und Verlustrechnung, Einkommens- und Umsatzsteuerbescheide. Bitte informieren Sie sich ausführlich, um einen möglichst präzisen Wert zu ermitteln. Die
          Unterhaltsberechnung hängt im Wesentlichen von Ihrem Einkommen ab. Für die Richtigkeit Ihrer Angaben können wir keine Verantwortung übernehmen.
        </p>
      </DialogModal>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "netIncome-alimonyPetitioner"}
        onClose={modalClose}
        title={"Was bedeutet Nettoeinkommen?"}
      >
        <p>
          Für die Berechnung ist es wichtig, dass nicht nur der aktuellste Monatslohn berücksichtigt wird. Sofern Sie also zusätzliche Gehaltskomponenten wie
          Jahres- / Quartalsbonus, Weihnachts- und/oder Urlaubsgeld erhalten hat, ermitteln Sie bitte den Durchschnitt der letzten 12 Monate und tragen Sie
          diesen in das Feld ein.
        </p>
      </DialogModal>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "netIncome-alimonyPartner"}
        onClose={modalClose}
        title={"Was bedeutet Nettoeinkommen?"}
      >
        <p>
          Für die Berechnung ist es wichtig, dass nicht nur der aktuellste Monatslohn berücksichtigt wird. Sofern der andere Elternteil also zusätzliche
          Gehaltskomponenten wie Jahres- / Quartalsbonus, Weihnachts- und/oder Urlaubsgeld erhalten hat, ermitteln Sie bitte den Durchschnitt der letzten 12
          Monate und tragen Sie diesen in das Feld ein.
        </p>
      </DialogModal>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "otherIncome"}
        onClose={modalClose}
        title={"Was sind sonstige monatliche Einkünfte (netto)?"}
      >
        <p>
          Aus Ihrer Steuererklärung geht hervor, welcher Betrag Ihnen nach Abzug der Einkommenssteuer übrig bleibt. Wenn Ihnen dazu keine Informationen
          vorliegen, ziehen Sie Ihren Einkommenssteuersatz ab.
        </p>
      </DialogModal>
      <DialogModal open={location.state && location.state.rentModalOpen === "livingExpenses"} onClose={modalClose} title={"Was zählt zur Warmmiete?"}>
        <p>Die Warmmiete setzt sich aus der Kaltmiete plus umlagefähige Nebenkosten (Hausmeister, Aufzug, Winterdienst, etc.) und Heizkosten zusammen.</p>
      </DialogModal>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "monthlyImputedRent-ownerWithMortgage"}
        onClose={modalClose}
        title={"Was ist der Wohnwert?"}
      >
        <p>
          Der Wohnwert ist der Betrag, für den die Wohnung üblicherweise vermietet werden könnte (Kaltmiete). Entscheidend sind hierfür neben dem lokalen
          Mietspiegel auch der Zustand und die Ausstattung der Wohnung bzw. des Hauses. Wenn Ihnen keine genauen Infos vorliegen, können Sie Ihre monatliche
          Kreditrate als Annäherungswert nehmen.
        </p>
      </DialogModal>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "monthlyImputedRent-ownerOutright"}
        onClose={modalClose}
        title={"Was ist der Wohnwert?"}
      >
        <p>
          Der Wohnwert ist der Betrag, für den die Wohnung üblicherweise vermietet werden könnte (Kaltmiete). Entscheidend sind hierfür neben dem lokalen
          Mietspiegel auch der Zustand und die Ausstattung der Wohnung bzw. des Hauses.
        </p>
      </DialogModal>
      <DialogModal open={location.state && location.state.rentModalOpen === "monthlyImputedRent-other"} onClose={modalClose} title={"Was ist der Wohnwert?"}>
        <p>
          Der Wohnwert ist der Betrag, für den die Wohnung üblicherweise vermietet werden könnte (Kaltmiete). Entscheidend sind hierfür neben dem lokalen
          Mietspiegel auch der Zustand und die Ausstattung der Wohnung bzw. des Hauses. Wenn Sie mit anderen Personen zusammen wohnen (z.B. bei den Eltern),
          berechnen Sie nur den anteiligen Wert für Sie selbst.
        </p>
      </DialogModal>
      <DialogModal
        open={location.state && location.state.rentModalOpen === "monthlyExpenses"}
        onClose={modalClose}
        title={"Was sind relevante monatliche Ausgaben?"}
      >
        <p>
          Für Schulden gilt, dass sie zwingend für die Lebenshaltung und die Erhaltung der Erwerbstätigkeit notwendig sein müssen. Sie müssen nachweisbar und
          vor der Trennung entstanden sein. Relevant für die Berechnung ist nicht der absolute Betrag der Schulden, sondern die (durchschnittliche) monatliche
          Rückzahlungsrate (Tilgung und Zins).
        </p>
        <p>
          Für Versicherungen gilt, dass sie notwendig und angemessen sein müssen. Sie dürfen lediglich der Absicherung der Existenz und nicht dem
          Vermögensaufbau dienen. Beispiele sind private Kranken- und Haftpflichtversicherungen.
        </p>
      </DialogModal>
    </Paper>
  );
}
